import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';

import { FaqApiService } from '@hiptraveler/data-access/api';

@Component({
  selector: 'app-support-ticket-modal',
  templateUrl: './support-ticket-modal.component.html',
  styleUrls: ['./support-ticket-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'support-ticket-modal'
  }
})
export class SupportTicketModalComponent implements OnInit, OnDestroy {
  supportForm: FormGroup;
  isSubmitted = false;
  isSuccess = false;
  uploadedFile: { url: string; filename: string } | null = null;
  ticketReference: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<SupportTicketModalComponent>,
    private faqApi: FaqApiService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    // Listen for file upload events from the directive
    window.addEventListener('fileUploaded', this.onFileUploaded as EventListener);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('fileUploaded', this.onFileUploaded as EventListener);
  }

  onFileUploaded = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const { url, filename } = customEvent.detail;
    this.uploadedFile = { url, filename };
    this.supportForm.patchValue({
      attachment: url
    });
  }

  onSubmit(): void {
    if (this.supportForm.valid) {
      this.isSubmitted = true;
      this.submitSupportTicket();
    } else {
      this.markFormGroupTouched();
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private submitSupportTicket(): void {
    const formValue = this.supportForm.value;
    const attachments = formValue.attachment ? [formValue.attachment] : [];

    this.faqApi.submitSupportTicket({
      name: formValue.name,
      email: formValue.email,
      message: formValue.message,
      attachments: attachments
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: any) => {
        this.isSubmitted = false;
        this.isSuccess = true;
        this.ticketReference = response.reference || null;
      },
      error: (error: any) => {
        this.isSubmitted = false;
        this.snackBar.open('Failed to submit support ticket. Please try again.', 'Close', {
          duration: 5000
        });
      }
    });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.supportForm.controls).forEach(key => {
      const control = this.supportForm.get(key);
      control?.markAsTouched();
    });
  }

  private initializeForm(): void {
    this.supportForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      message: ['', [Validators.required]],
      attachment: [null]
    });
  }

}
